// Accessibility utilities and helpers
export const a11y = {
  // Focus management
  trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    element.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => {
      element.removeEventListener('keydown', handleTabKey);
    };
  },

  // Announce to screen readers
  announce(message, priority = 'polite') {
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';
    announcer.textContent = message;
    
    document.body.appendChild(announcer);
    
    setTimeout(() => {
      document.body.removeChild(announcer);
    }, 1000);
  },

  // Check color contrast
  checkContrast(foreground, background) {
    const getLuminance = (color) => {
      const rgb = color.match(/\d+/g);
      if (!rgb) return 0;
      
      const [r, g, b] = rgb.map(c => {
        c = parseInt(c) / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });
      
      return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    };

    const l1 = getLuminance(foreground);
    const l2 = getLuminance(background);
    const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
    
    return {
      ratio: ratio.toFixed(2),
      wcagAA: ratio >= 4.5,
      wcagAAA: ratio >= 7
    };
  },

  // Keyboard navigation helpers
  handleArrowNavigation(event, items, currentIndex, onSelect) {
    let newIndex = currentIndex;
    
    switch (event.key) {
      case 'ArrowDown':
        newIndex = Math.min(currentIndex + 1, items.length - 1);
        break;
      case 'ArrowUp':
        newIndex = Math.max(currentIndex - 1, 0);
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = items.length - 1;
        break;
      case 'Enter':
      case ' ':
        onSelect(items[currentIndex]);
        return;
      default:
        return;
    }
    
    event.preventDefault();
    onSelect(items[newIndex]);
  }
};

// Screen reader utilities
export const screenReader = {
  // Generate descriptive text for complex UI elements
  describeTable(rows, columns) {
    return `Table with ${rows} rows and ${columns} columns`;
  },

  describeChart(type, dataPoints) {
    return `${type} chart with ${dataPoints} data points`;
  },

  describeStatus(status) {
    const statusDescriptions = {
      submitted: 'Application submitted and awaiting review',
      under_review: 'Application is currently being reviewed',
      approved: 'Application has been approved',
      rejected: 'Application has been rejected',
      enrolled: 'Student has been enrolled'
    };
    return statusDescriptions[status] || status;
  }
};

// High contrast mode detection
export const detectHighContrast = () => {
  return window.matchMedia('(prefers-contrast: high)').matches;
};

// Reduced motion detection
export const detectReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Focus visible polyfill
export const setupFocusVisible = () => {
  let hadKeyboardEvent = true;
  const keyboardThrottleTimeout = 100;

  const onPointerDown = () => {
    hadKeyboardEvent = false;
  };

  const onKeyDown = (e) => {
    if (e.metaKey || e.altKey || e.ctrlKey) {
      return;
    }
    hadKeyboardEvent = true;
  };

  const onFocus = (e) => {
    if (hadKeyboardEvent || e.target.matches(':focus-visible')) {
      e.target.classList.add('focus-visible');
    }
  };

  const onBlur = (e) => {
    e.target.classList.remove('focus-visible');
  };

  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('mousedown', onPointerDown, true);
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('touchstart', onPointerDown, true);
  document.addEventListener('focus', onFocus, true);
  document.addEventListener('blur', onBlur, true);
};